import React, { useState } from 'react'
import { X } from 'lucide-react'
import { userKanriService } from '../../services/userKanriService'
import { useApiError } from '../../../shared/hooks/useApiError'
import Button from '../../../shared/ui/Button'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import MessageDialog from '../../../shared/ui/MessageDialog'

// Zod schema for pix-label invite form validation
const inviteSchema = z.object({
  serialNo1: z.string().min(1, 'IDの前半部分は必須項目です').length(4, '4桁の数字を入力してください'),
  serialNo2: z.string().min(1, 'IDの後半部分は必須項目です').length(4, '4桁の数字を入力してください')
})

const PixLabelInviteModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  userCount = 0,
  riyoPlan = "4",
  onError
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')
  const [dialogTitle, setDialogTitle] = useState('')
  const [dialogType, setDialogType] = useState('info')
  
  // Error handling - use parent's error handling if provided, otherwise use local
  const { handleApiError: localHandleApiError, handleApiResponse } = useApiError()
  const handleApiError = onError || localHandleApiError

  // Use React Hook Form with Zod resolver
  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(inviteSchema),
    mode: 'onSubmit',
    defaultValues: {
      serialNo1: '',
      serialNo2: ''
    }
  })

  const onSubmit = async (data) => {
    // Check user limit based on plan
    const maxUsers = {
      '1': 1,
      '2': 5,
      '3': 30,
      '4': Infinity
    }
    
    if (userCount >= maxUsers[riyoPlan]) {
      setDialogTitle('エラー')
      setDialogMessage('追加出来るユーザーの数を超えています。')
      setDialogType('error')
      setShowDialog(true)
      return
    }

    try {
      setIsLoading(true)
      
      const serialNo = data.serialNo1 + data.serialNo2
      
      // Check if ID exists and is not already registered
      const countResponse = await userKanriService.getPixtagUserCompanyCount(serialNo)
      
      if (countResponse.userCount === 0) {
        setDialogTitle('エラー')
        setDialogMessage('存在しないIDです。')
        setDialogType('error')
        setShowDialog(true)
        return
      }
      
      if (countResponse.companyCount !== 0) {
        setDialogTitle('エラー')
        setDialogMessage('すでに企業登録済みのIDです。')
        setDialogType('error')
        setShowDialog(true)
        return
      }
      
      // Get pix-label user data
      const userResponse = await userKanriService.getPixtagUser(serialNo)
      
      // Register the link
      const renkeiResponse = await userKanriService.addPixRenkei({
        prePixId: userResponse.prePixId,
        pixUserId: userResponse.pixUserId
      })
      
      // Check if response has error
      if (handleApiResponse(renkeiResponse)) {
        return // Error was handled, exit early
      }
      
      setDialogTitle('成功')
      setDialogMessage('招待が完了しました。\npix-labelの「設定」をご確認ください。')
      setDialogType('success')
      setShowDialog(true)
      onSuccess()
      reset({
        serialNo1: '',
        serialNo2: ''
      })
      //onClose()
    } catch (error) {
      console.error('Failed to invite user:', error)
      handleApiError(error, '招待に失敗しました。')
    } finally {
      setIsLoading(false)
    }
  }

  const onFormError = (errors) => {
    console.log('Form validation errors:', errors)
  }

  const handleClose = () => {
    reset({
      serialNo1: '',
      serialNo2: ''
    })
    onClose()
  }

  const handleDialogClose = () => {
    setShowDialog(false)
    if (dialogType === 'success') {
      onClose()
    }
  }

  // Simple utility to get border class based on error state
  const getBorderClass = (fieldName, baseClass) => {
    if (errors[fieldName]) {
      return `${baseClass} border border-red-500 bg-red-50`
    }
    return `${baseClass} border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent`
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800">
            pix-labelユーザー招待
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleFormSubmit(onSubmit, onFormError)} className="space-y-6">
          <div className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
            pix-labelの「設定」にあるIDを入力してください。
          </div>

          <div>
            <label htmlFor="serialNo1" className="block text-sm font-medium text-gray-700 mb-2">
              ID
            </label>
            <div className="flex items-center space-x-2">
              <input
                id="serialNo1"
                type="text"
                {...register('serialNo1')}
                maxLength={4}
                className={getBorderClass('serialNo1', 'w-20 px-3 py-2 rounded-lg text-center')}
                placeholder="0000"
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                  setValue('serialNo1', value)
                }}
              />
              <span className="text-gray-500">-</span>
              <input
                id="serialNo2"
                type="text"
                {...register('serialNo2')}
                maxLength={4}
                className={getBorderClass('serialNo2', 'w-20 px-3 py-2 rounded-lg text-center')}
                placeholder="0000"
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                  setValue('serialNo2', value)
                }}
              />
            </div>
            <div className="mt-2">
              <p className="text-sm text-red-600">{errors.serialNo1 ? errors.serialNo1.message : '\u00A0'}</p>
              <p className="text-sm text-red-600">{errors.serialNo2 ? errors.serialNo2.message : '\u00A0'}</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              size="md"
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={isLoading}
            >
              招待
            </Button>
          </div>
        </form>
      </div>

      {/* Message Dialog */}
      <MessageDialog
        isOpen={showDialog}
        title={dialogTitle}
        message={dialogMessage}
        type={dialogType}
        onClose={handleDialogClose}
      />
    </div>
  )
}

export default PixLabelInviteModal
